import {
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  Checkbox,
  FontAwesomeIcon,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { Search } from "@/components/search/Search.tsx";
import { ColumnConfig, ColumnDefinition } from "@/types/column.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ManageColumnsModalProps extends ModalProps {
  columnConfig: ColumnConfig;
  visibleColumns: string[];
  onSubmit: (updatedVisibleColumns: ColumnDefinition[]) => void;
}

export function ManageColumnsModal({
  columnConfig,
  visibleColumns,
  onSubmit,
  ...props
}: ManageColumnsModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };
  const [searchTerm, setSearchTerm] = useState("");

  const [showAllColumns, setShowAllColumns] = useState(visibleColumns.length === 0);
  const [currentVisibleColumns, setCurrentVisibleColumns] = useState(visibleColumns);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(
    columnConfig.categories.reduce<Record<string, boolean>>((acc, category) => {
      acc[category.id] = false;
      return acc;
    }, {})
  );

  const allColumns = useMemo(
    () => columnConfig.categories.flatMap((c) => c.columns),
    [columnConfig]
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value) {
      const filteredIds = new Set(
        allColumns
          .filter((col) => col.title?.toLowerCase().includes(value.toLowerCase()))
          .map((fc) => fc.id)
      );

      const expanded = columnConfig.categories.reduce(
        (acc, category) => {
          acc[category.id] = category.columns.some((col) => filteredIds.has(col.id));
          return acc;
        },
        {} as Record<string, boolean>
      );

      setExpandedCategories(expanded);
    }
  };

  const mappedCurrentConfigColumns = useMemo(() => {
    const allColumns = columnConfig.categories.flatMap((category) => category.columns);
    return currentVisibleColumns
      .map((colId) => allColumns.find((col) => col.id === colId))
      .filter(Boolean) as ColumnDefinition[];
  }, [currentVisibleColumns, columnConfig]);

  const handleUpdate = () => {
    setShowAllColumns(false);
    onSubmit(mappedCurrentConfigColumns);
    close(id);
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleColumnSelectionChange = (columnId: string, isSelected: boolean) => {
    const nextVisibleColumns = isSelected
      ? [...currentVisibleColumns, columnId]
      : currentVisibleColumns.filter((colId) => colId !== columnId);
    setCurrentVisibleColumns(nextVisibleColumns);
    if (nextVisibleColumns.length === 0) {
      setShowAllColumns(true);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reorderedColumns = Array.from(currentVisibleColumns);
    const [movedItem] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, movedItem);
    setCurrentVisibleColumns(reorderedColumns);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setShowAllColumns(false);
  };

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("common:manage_columns")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      actions={
        <Stack gap={"sm"}>
          {showAllColumns ? (
            <Button size={"lg"} onClick={() => setShowAllColumns(false)}>
              {t("common:ok")}
            </Button>
          ) : (
            <Button type={"submit"} size={"lg"} onClick={handleUpdate}>
              {t("common:save")}
            </Button>
          )}
        </Stack>
      }
    >
      <Stack gap="md">
        <Search
          value={searchTerm}
          placeholder={t("core:add_or_search_field") + "..."}
          clearable
          onClear={clearSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => setShowAllColumns(true)}
        />

        {!showAllColumns ? (
          <Stack>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId={"selected-columns"}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {mappedCurrentConfigColumns.map((column, index) => {
                      if (column && !column?.isFixed) {
                        return (
                          <Draggable key={column.id} draggableId={column.id} index={index}>
                            {(provided) => (
                              <Box
                                className={
                                  "flex items-center justify-between gap-md my-2 px-4 border border-dark/5 rounded-xs bg-dark/5"
                                }
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Stack direction={"horizontal"} gap={"md"}>
                                  <Checkbox
                                    checked={currentVisibleColumns.some((col) => col === column.id)}
                                    onChange={(e) =>
                                      handleColumnSelectionChange(column.id, e.target.checked)
                                    }
                                    title={column.title}
                                  />
                                </Stack>
                                <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical mr-2" />
                              </Box>
                            )}
                          </Draggable>
                        );
                      }
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <Button
              variant="plain"
              size="md"
              leftIcon={<FontAwesomeIcon icon="fa-solid fa-plus" />}
              onClick={() => setShowAllColumns(true)}
              className={"font-semibold"}
            >
              {t("common:add_field")}
            </Button>
          </Stack>
        ) : (
          columnConfig.categories.map((category) => (
            <div key={category.id} className="category">
              <Box
                className="flex items-center justify-between px-0.5 cursor-pointer font-semibold"
                onClick={() => toggleCategory(category.id)}
              >
                <Title size={"xs"} header={category.title} />
                {expandedCategories[category.id] ? (
                  <FontAwesomeIcon icon={"fa-light fa-chevron-down"} />
                ) : (
                  <FontAwesomeIcon icon={"fa-light fa-chevron-right"} />
                )}
              </Box>
              {expandedCategories[category.id] && (
                <Stack>
                  {category.columns
                    .filter((column) =>
                      searchTerm
                        ? column.title?.toLowerCase().includes(searchTerm.toLowerCase())
                        : true
                    )
                    .map((column) => (
                      <Stack
                        direction={"horizontal"}
                        gap={"md"}
                        key={column.id}
                        className={"py-2 px-0.5 border-b border-dark/5 last:border-b-0"}
                      >
                        <Stack direction={"horizontal"} gap={"md"} className={"flex items-center"}>
                          <Checkbox
                            key={column.id}
                            id={`Checkbox-${column.id}`}
                            checked={!!currentVisibleColumns.find((col) => col === column.id)}
                            onChange={(e) =>
                              handleColumnSelectionChange(column.id, e.target.checked)
                            }
                            title={column.title}
                          />
                        </Stack>
                      </Stack>
                    ))}
                </Stack>
              )}
            </div>
          ))
        )}
      </Stack>
    </Modal>
  );
}
